import React, { useState, useEffect } from 'react';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import styles from '../../settings_sidebar/datasource/DatasourceSettings.module.scss';
import { FaIcon } from "../../../common/Icons.jsx";
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from "react-redux";
import {cloneDeep} from 'lodash';
import CollapsibleSection from "../../../common/CollapsibleSection.jsx";
import Dialog, { DialogFooter, DialogHeader } from "../../../bootstrap/Dialog.jsx";
import { Portal } from "../../../common/Portal.jsx";
import { closeModal, closeSidebar, openModal, saveWidgetSettings } from "../../redux/dashboardSlice.js";
import { DropArea, DragHandle } from "../../../common/DragNDrop.jsx";
import { HOVER_THRESHOLD } from "./constants.js";

function SettingsDialog({InnerComponent, ...props}) {
    return (
        <Dialog {...props}>
            {InnerComponent}
        </Dialog>
    )
}

function MapVisualisationWidgetSettingsOption({
    title, 
    description,
    SettingComponent, 
    addSetting = null,
    dnd_item_type = '',
    fieldName,
    optionName,
    widgetId
}) {
    const dispatch = useDispatch();
    const setAttribute = useSetWidgetAttribute(widgetId);
    const loading = useSelector(state => state.dashboard.loading);
    const settings = useSelector(state => state.dashboard.widgets[widgetId].settings) || {};
    // Doesn't need global setting because modal already hide the map, also, some setting need to be set before others.
    const [localSettings, setLocalSettings] = useState(settings);
    const fields = localSettings[fieldName];
    const [sourceIndex, setSourceIndex] = useState();
    const toggleModal = (index) => {
      if (index != null) {
          dispatch(openModal());
      } else {
          dispatch(closeModal());
      }
      setSourceIndex(index)
    }

    const saveSettings = async () => {
      setAttribute('settings', localSettings)
      await dispatch(saveWidgetSettings());
      toggleModal(null);
      dispatch(closeSidebar());
    }

    const updateField = (baseName, value) => {
      let fieldNames = baseName.split('.')
      const fieldName = fieldNames[0];
      const index = fieldNames[1]
      let sourceFields = cloneDeep(localSettings[fieldName]) || [];

      let item = { ...sourceFields[index] };
      let current = item;
      let keys = fieldNames.slice(2, fieldNames.length)
      for (let i = 0; i < keys.length-1; i++){
          const keyPart = keys[i]
          if (!current[keyPart]){
          current[keyPart] = {}
          }
          current = current[keyPart]
      }
      current[keys[keys.length-1]] = value;

      sourceFields[index] = item;
      setLocalSettings(prev => ({
        ...prev,
        [fieldName]: sourceFields
      }))
    }

    const removeField = (index, fieldName) => {
      let sourceFields = cloneDeep(settings[fieldName]);
      sourceFields.splice(index, 1)
      setAttribute(`settings.${fieldName}`, sourceFields);
    };

    const moveField = (draggedIndex, targetIndex, fieldName) => {
      let sourceFields = [...settings[fieldName]];
      const [d] = sourceFields.splice(draggedIndex, 1);
      sourceFields.splice(targetIndex, 0, d);
      setAttribute(`settings.${fieldName}`, sourceFields);
    }

    useEffect(() => {
        // Update local setting if anything is added, move, remove
        setLocalSettings(prev => ({...prev, [fieldName]: settings[fieldName]}))
    }, [settings])

    const settingElems = (localSettings[fieldName] || []).map((f, index) =>  {
      return (
        <DropArea
            key={f.id}
            accept={dnd_item_type}
            item={{id: f.id, index, type: dnd_item_type}}
            onMove={(drag_i, target_i) => {moveField(drag_i, target_i, fieldName)}}
            hoverThreshold={HOVER_THRESHOLD}
            render={({dropRef, dragRef, isDragging}) => (
                <li ref={dropRef} className={`${isDragging ? "is-dragging" : ""} widget-container list-group-item ${styles['list-group-item']} mb-1`}>
                    <DragHandle sortHandleClass={styles['sort-handle']} dragRef={dragRef} />
                    <div className={styles['list-group-item-content']}>
                        <div className={styles['list-group-item-description']}>
                            <h4>{f?.[optionName] || I18n.t('unnamed')}</h4>
                        </div>
                    </div>
                    <div className={styles['list-group-item-actions']}>
                        <a onClick={() => toggleModal(index, fieldName)}>
                            <FaIcon type='cog' style={{color: 'black'}} />
                        </a>
                        {
                            addSetting && 
                            <a onClick={(e) => removeField(index, fieldName)}>
                                <FaIcon type='trash' className='text-danger' />
                            </a>
                        }
                    </div>
                </li>
            )}
        >
        </DropArea>
      )
    }).filter(item => item !== null)
    return (
      <>
        <hr />
        {sourceIndex != null && <Portal
            component={SettingsDialog}
            /* SettingsDialog props */
            header={<DialogHeader showBorder={true} title={I18n.t('edit_settings')} onClose={() => toggleModal()} />}
            footer={<DialogFooter onAdd={saveSettings} onClose={() => toggleModal()} addText={loading ? I18n.t('loading') : I18n.t('save')} />}
            disabled={loading}
            size='md'
            closeOnOutsideClick={true}
            onClose={() => toggleModal()}
            InnerComponent={
                <SettingComponent
                    data={fields[sourceIndex]}
                    index={sourceIndex}
                    settings={localSettings}
                    {...{updateField, widgetId}}
                />
            }
        />}
        <CollapsibleSection title={title} description={description}>
            <DndProvider backend={Backend}>
                <ul className={`list-group mt-1`} key={fieldName}>
                    {settingElems}
                </ul>
            </DndProvider>
            {addSetting != null &&
            <fieldset>
                <button className="btn btn-primary" onClick={addSetting}>
                    {I18n.t("add")}
                </button>
            </fieldset>
            }
        </CollapsibleSection>
    </>
    )
}

export default MapVisualisationWidgetSettingsOption;